import React, { useEffect } from 'react'
import { useAPI } from '../../Contexts/APIContext'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { useAllReportsStore } from '../../Storage/allReports/useAllReportsStore'
import { useNavigate, useLocation } from 'react-router-dom'
import ReportsTable from './components/ReportsTable'
import ControlPanel from './components/ControlPanel'
import Loader from '../../Components/GeneralComponents/Loader'
import classes from './index.module.css'

const AllReports = () => {
  const { getAllReports } = useAPI()
  const { ref, inView } = useInView()
  const navigate = useNavigate()
  const location = useLocation()

  // zustand storage
  const filterByObject = useAllReportsStore(state => state.selectedOption.id)
  const filterByStatus = useAllReportsStore(state => state.selectedStatus.id)
  const filterByResolved = useAllReportsStore(state => state.isResolved)

  const setSelectedOption = useAllReportsStore(state => state.setSelectedOption)
  const setSelectedStatus = useAllReportsStore(state => state.setSelectedStatus)
  const setIsResolved = useAllReportsStore(state => state.setIsResolved)
  const options = useAllReportsStore(state => state.options)
  const statuses = useAllReportsStore(state => state.statuses)

  const {
    data,
    isError,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['reports', filterByObject, filterByStatus, filterByResolved],
    queryFn: ({ pageParam = 1 }) =>
      getAllReports({
        o: filterByObject,
        s: filterByStatus,
        r: filterByResolved,
        p: pageParam,
      }),
    getNextPageParam: pageData => {
      const nextPage = pageData.p + 1
      const totalReports = pageData.reports_count
      const pageSize = pageData.n
      const fetchedCount = pageData.p * pageSize

      return fetchedCount < totalReports ? nextPage : undefined
    },
    keepPreviousData: true,
  })

  const allReports = data?.pages.flatMap(page => page.reports) || []

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, allReports])

  useEffect(() => {
    const params = new URLSearchParams()
    if (filterByObject) params.set('object', filterByObject)
    if (filterByStatus) params.set('status', filterByStatus)
    if (filterByResolved) params.set('resolved', filterByResolved)
    navigate({ search: params.toString() }, { replace: true })
  }, [filterByObject, filterByStatus, filterByResolved])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const object = params.get('object') || ''
    const status = params.get('status') || ''
    const resolved = params.get('resolved') === 'true'

    const foundOption = options.find(opt => opt.id === object) || options[0]
    setSelectedOption(foundOption)
    const foundStatus = statuses.find(st => st.id === status) || statuses[0]
    setSelectedStatus(foundStatus)
    setIsResolved(resolved)
  }, [])

  return (
    <div className={classes.wrapper}>
      <h1 className={classes.header}>All Reports</h1>

      <ControlPanel isFetching={isFetching} />
      {isLoading || (isFetching && !isFetchingNextPage) ? (
        <Loader isLoading={true} />
      ) : (
        <ReportsTable
          reports={allReports}
          lastRowRef={ref}
          isError={isError}
          error={error}
        />
      )}
    </div>
  )
}

export default AllReports
